/*
Global Search API client — POST /api/v2/search on restServer
(api2.rundiffusion.com).

Auth mirrors the rest of the v2 client surface (libraryApi.ts, webClientDevice.ts):
Firebase ID token bearer, and the account rides the request itself as the `team_id`
QUERY parameter — omitted entirely for the personal account. v1's `X-Account-Id`
header is not read by v2 and is no longer sent.

`team_id` must stay on the query string even though this is a POST. Account
Selection happens inside the server's authentication class, before any parser
touches the body, so a `team_id` form field would be invisible to it and the call
would quietly act on the personal account.

`X-Device-Id` identifies this browser as the `web_app` client kind. Search is an
undocumented web-app surface, so this client requires registration before sending the
request. Registration is shared with the rest of the app and costs nothing after the
first call because the id is cached in localStorage.

The server is authoritative for every queried category; this client only transports.
AbortSignal + a monotonic sequence in the modal handle cancellation/stale responses.

The request is ALWAYS multipart/form-data (never JSON): scalar fields map to
same-named parts, `types` is sent as repeated parts, and an optional `image`
part turns the same request into a reverse-image search. We must NOT set a
Content-Type header — the browser sets the multipart boundary itself.
*/
import { auth } from '@/firebase';
import {
	SearchRequest,
	SearchResponse,
} from '@/types/search';
import {
	ensureWebClientDeviceId,
	forgetWebClientDeviceIfRejected,
} from '@/utils/webClientDevice';

const getBaseUrl = (): string => (
	(process.env.VUE_APP_API_BASE_URL as string) || 'https://api2.rundiffusion.com/api/v2'
).replace(/\/+$/, '');

export class SearchApiError extends Error {
	status: number;
	code: string | null;

	constructor (message: string, status: number, code: string | null = null) {
		super(message);
		// Restore the prototype chain by hand. We compile to ES5, where
		// `super(message)` returns a plain Error and silently discards this
		// subclass, so `err instanceof SearchApiError` would be false for every
		// error this module throws.
		Object.setPrototypeOf(this, SearchApiError.prototype);
		this.name = 'SearchApiError';
		this.status = status;
		this.code = code;
	}
}

/**
 * The endpoint URL for an account. A team is named in `team_id`; the personal
 * account is the absence of the parameter, not a sentinel value.
 */
export const buildSearchUrl = (baseUrl: string, teamId: string | null): string => {
	const params = new URLSearchParams();
	if (teamId) params.set('team_id', teamId);
	const query: string = params.toString();
	return `${baseUrl}/search${query ? `?${query}` : ''}`;
};

export const searchAll = async (
	input: SearchRequest,
	teamId: string | null, // null → personal account (the active Account Selection)
	signal?: AbortSignal,
): Promise<SearchResponse> => {
	const user = auth?.currentUser || null;
	if (!user) throw new SearchApiError('Not signed in', 401);
	const token: string = await user.getIdToken();
	const baseUrl: string = getBaseUrl();

	// Cached: a no-op after the first successful registration anywhere in the
	// app. Search is web-app-only, so a null id must stop before the API call.
	const deviceId: string | null = await ensureWebClientDeviceId({
		baseUrl,
		idToken: token,
	});
	if (!deviceId) {
		throw new SearchApiError(
			'Search could not register this browser. Please try again.',
			503,
			'WEB_APP_REGISTRATION_REQUIRED',
		);
	}

	const form = new FormData();
	if (input.query) form.append('query', input.query);
	if (input.types) for (const type of input.types) form.append('types', type);
	if (input.image) form.append('image', input.image);

	const headers: Record<string, string> = {
		// No Content-Type — the browser sets multipart/form-data + boundary.
		'Authorization': `Bearer ${token}`,
		'X-Device-Id': deviceId,
	};

	const response = await fetch(buildSearchUrl(baseUrl, teamId), {
		method: 'POST',
		headers,
		body: form,
		signal,
	});

	if (!response.ok) {
		let code: string | null = null;
		let message: string = `Search failed (${response.status})`;
		try {
			const body = await response.json();
			code = body?.error?.code || null;
			message = body?.error?.message || message;
		} catch (e) { /* non-JSON error body — keep defaults */ }
		// A revoked device fails EVERY request, before the caller is even
		// resolved. Forget the id here so the next search registers a fresh one,
		// rather than replaying the dead one until site data is cleared by hand.
		forgetWebClientDeviceIfRejected(code);
		throw new SearchApiError(message, response.status, code);
	}
	return await response.json() as SearchResponse;
};
